import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, orthoUrl, rawUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Slider = ({ label, value, min, max, step, onChange, id }) => (
  <label className="block text-xs text-slate-400">
    <div className="flex justify-between"><span>{label}</span><span className="font-mono text-slate-300">{value}</span></div>
    <input type="range" data-testid={id} min={min} max={max} step={step} value={value} onChange={(e) => onChange(+e.target.value)} className="w-full accent-blue-600" />
  </label>
);

export const AlignDialog = ({ upload, address, baselineYear, onClose, onSaved }) => {
  const [t, setT] = useState({ tx: 0, ty: 0, scale: 1, rot: 0, opacity: 0.6, ...(upload.transform || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setT({ ...t, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/uploads/${upload.id}/align`, t);
      toast.success("Uitlijning opgeslagen");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-slate-900/95 backdrop-blur-md border-slate-700" data-testid="align-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Beeld uitlijnen op luchtfoto {baselineYear}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-[1fr_260px] gap-5">
          <div className="relative aspect-square rounded-md overflow-hidden border border-slate-700 bg-black">
            <img src={orthoUrl(address.id, baselineYear)} alt="basis" className="absolute inset-0 w-full h-full object-cover" />
            <img src={rawUrl(upload.id)} alt="eigen beeld" data-testid="align-overlay-img" className="absolute max-w-none pointer-events-none"
              style={{ width: `${t.scale * 100}%`, left: `${(0.5 + t.tx) * 100}%`, top: `${(0.5 + t.ty) * 100}%`, opacity: t.opacity, transform: `translate(-50%, -50%) rotate(${t.rot}deg)` }} />
          </div>
          <div className="space-y-4">
            <p className="text-xs text-slate-400 leading-relaxed">Schuif, schaal en draai het eigen beeld tot daken en perceelgrenzen samenvallen met de luchtfoto. Daarna kan het beeld met de basis vergeleken worden.</p>
            <Slider id="align-tx" label="Horizontaal" value={t.tx} min={-0.5} max={0.5} step={0.005} onChange={set("tx")} />
            <Slider id="align-ty" label="Verticaal" value={t.ty} min={-0.5} max={0.5} step={0.005} onChange={set("ty")} />
            <Slider id="align-scale" label="Schaal" value={t.scale} min={0.2} max={4} step={0.01} onChange={set("scale")} />
            <Slider id="align-rot" label="Rotatie (°)" value={t.rot} min={-180} max={180} step={0.5} onChange={set("rot")} />
            <Slider id="align-opacity" label="Transparantie" value={t.opacity} min={0} max={1} step={0.05} onChange={set("opacity")} />
            <button data-testid="align-save-btn" onClick={save} disabled={busy} className="w-full h-10 rounded-md bg-blue-600 hover:bg-blue-500 text-sm font-medium disabled:opacity-60">
              {busy ? "Opslaan…" : "Uitlijning opslaan"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
